import type { ReactNode } from 'react';
import { MessageCircle } from 'lucide-react';

type PublicBridgeHeroProps = {
  title: string;
  description: ReactNode;
  primaryLabel: string;
  primaryHref: string;
};

export function PublicBridgeHero({
  title,
  description,
  primaryLabel,
  primaryHref,
}: PublicBridgeHeroProps) {
  return (
    <section className="relative isolate min-h-[700px] overflow-hidden bg-doq-surface sm:min-h-[660px] lg:min-h-[680px]">
      <picture className="absolute inset-0 -z-10 block overflow-hidden" aria-hidden="true">
        <source media="(max-width: 639px)" srcSet="/images/doq-hero-arch-mobile.webp" />
        <source media="(min-width: 640px)" srcSet="/images/doq-hero-arch-desktop.webp" />
        <img
          src="/images/doq-hero-arch-desktop.webp"
          alt=""
          width="1672"
          height="941"
          className="absolute inset-x-0 top-10 h-[112%] w-full object-cover object-top sm:top-0 sm:h-full sm:object-[50%_35%]"
          loading="eager"
          fetchPriority="high"
        />
      </picture>

      <div className="relative z-10 mx-auto min-h-[700px] max-w-[1540px] px-6 pt-[112px] sm:min-h-[660px] sm:px-8 sm:pt-[150px] lg:min-h-[680px] lg:pl-[30%] lg:pr-16 lg:pt-[156px] xl:pl-[31%] xl:pr-24 xl:pt-[168px]">
        <div className="max-w-[560px]">
          <h1 className="max-w-[560px] text-[clamp(2rem,9vw,2.25rem)] font-semibold leading-[1.14] text-doq-green sm:whitespace-pre-line sm:text-[52px] sm:leading-[1.06] lg:text-[48px] xl:text-[52px]">
            {title.split('\n').map((line) => (
              <span key={line} className="block whitespace-nowrap">
                {line}
              </span>
            ))}
          </h1>
          <div className="mt-4 min-w-0 max-w-[calc(100vw_-_3rem)] break-words text-base leading-7 text-doq-muted sm:mt-6 sm:max-w-[650px] sm:leading-8">
            {description}
          </div>
        </div>

        <div className="absolute inset-x-6 bottom-36 flex flex-col gap-3 sm:static sm:mt-7 sm:flex-row sm:items-center sm:gap-6">
          <a
            href={primaryHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-[52px] items-center justify-center gap-2 bg-doq-green px-6 text-sm font-semibold text-[#fffaf2] shadow-[0_12px_28px_rgba(9,46,40,0.16)] transition hover:-translate-y-0.5 hover:bg-[#0a4b42] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-doq-copper active:translate-y-px sm:w-auto"
          >
            <MessageCircle className="size-5" strokeWidth={1.8} />
            {primaryLabel}
          </a>
        </div>
      </div>
    </section>
  );
}
